import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Avatar, Box, Button, Card, CircularProgress, Container, Paper, Stack, TextField, Typography } from "@mui/material";
import { LockOpen } from "@mui/icons-material";
import api from "lib/api/api";

export default function PublicAusbildungRoomPage() {
  const { token } = useParams();
  const [password, setPassword] = useState("");
  const [room, setRoom] = useState(null);
  const [photos, setPhotos] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const unlock = async event => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post(`/public/ausbildung-rooms/${token}/unlock`, { password });
      setRoom(data);
    } catch (e) {
      setError(e.response?.data?.message || "The link or password is invalid.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!room) return undefined;
    let active = true;
    Promise.all((room.applicants || []).filter(applicant => applicant.photoAvailable).map(async applicant => {
      try {
        const { data } = await api.get(`/public/ausbildung-rooms/${token}/applicants/${applicant.id}/photo`, {
          responseType: "blob", headers: { "X-Room-Password": password }, skipAuthExpiry: true
        });
        return [applicant.id, URL.createObjectURL(data)];
      } catch { return [applicant.id, ""]; }
    })).then(entries => {
      if (active) setPhotos(Object.fromEntries(entries.filter(([, url]) => url)));
      else entries.forEach(([, url]) => url && URL.revokeObjectURL(url));
    });
    return () => { active = false; };
  }, [room, token, password]);

  useEffect(() => () => Object.values(photos).forEach(URL.revokeObjectURL), [photos]);

  const downloadCv = async applicant => {
    try {
      const { data } = await api.get(`/public/ausbildung-rooms/${token}/applicants/${applicant.id}/cv`, {
        responseType: "blob", headers: { "X-Room-Password": password }, skipAuthExpiry: true
      });
      const url = URL.createObjectURL(data);
      const anchor = document.createElement("a");
      anchor.href = url; anchor.download = `ALGermanist-Lebenslauf-${(applicant.name || "Applicant").replace(/[^a-z0-9_-]/gi, "-")}.pdf`;
      document.body.appendChild(anchor); anchor.click(); anchor.remove(); URL.revokeObjectURL(url);
    } catch { setError("The generated Lebenslauf could not be downloaded. Please try again."); }
  };

  return <Container maxWidth="md" dir="ltr" sx={{ py: { xs: 3, md: 6 }, direction: "ltr" }}>
    <Paper elevation={3} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 5, border: "1px solid #e4ebe8" }}>
      <Box component="img" src="/al-germanist-logo.png" alt="ALGermanist" sx={{ width: 150, display: "block", mx: "auto", mb: 3 }} />
      {!room ? <Box component="form" onSubmit={unlock} sx={{ maxWidth: 430, mx: "auto", textAlign: "center" }}>
        <Avatar sx={{ bgcolor: "#e5f3ef", color: "#216658", mx: "auto", mb: 1.5 }}><LockOpen /></Avatar>
        <Typography variant="h5" fontWeight={900} sx={{ mb: 1 }}>Protected applicant room</Typography>
        <Typography color="text.secondary" sx={{ mb: 2.5 }}>Enter the password from the ALGermanist email to view this category room.</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField fullWidth required type="password" label="Room password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} />
        <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 2, py: 1.2 }}>{loading ? <CircularProgress size={22} color="inherit" /> : "Open room"}</Button>
      </Box> : <>
        <Typography variant="h4" textAlign="center" fontWeight={900} sx={{ color: "#1f5e50", mb: 1 }}>{room.categoryName}</Typography>
        <Typography textAlign="center" color="text.secondary" sx={{ mb: 3 }}>Approved applicants</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!room.applicants?.length ? <Alert severity="info">There are no approved applicants in this room.</Alert> :
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(3,minmax(0,1fr))" }, gap: 2 }}>
            {room.applicants.map(person => <Card key={person.id} variant="outlined" sx={{ p: 2, borderRadius: 4, textAlign: "center", borderColor: "#e0eae6" }}>
              <Avatar src={photos[person.id] || undefined} sx={{ width: 96, height: 96, mx: "auto", mb: 1.25, bgcolor: person.gender === "female" ? "#c3547e" : person.gender === "male" ? "#377099" : "#4a7569", fontSize: 32, fontWeight: 800 }}>
                {(person.name || "?").trim().charAt(0).toLocaleUpperCase()}
              </Avatar>
              <Typography fontWeight={900} noWrap>{person.name || "Applicant"}</Typography>
              <Stack spacing={.4} sx={{ mt: 1 }}>
                {person.gender && <Typography variant="body2" color="text.secondary">{person.gender === "female" ? "Female" : person.gender === "male" ? "Male" : person.gender === "other" ? "Diverse" : "Not specified"}</Typography>}
                {person.maritalStatus && <Typography variant="body2" color="text.secondary">{person.maritalStatus}</Typography>}
              </Stack>
              {person.cvAvailable ? <Button fullWidth size="small" variant="contained" sx={{ mt: 1.5 }} onClick={() => downloadCv(person)}>Download Lebenslauf</Button> : <Typography variant="body2" color="warning.dark" fontWeight={700} sx={{ mt: 1.5 }}>Lebenslauf in progress</Typography>}
            </Card>)}
          </Box>}
      </>}
    </Paper>
  </Container>;
}
